import {useState} from 'react';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {Grid, Container, Header, Image, List, Message, Popup, Icon} from 'semantic-ui-react';

import httpClient from 'httpClient';
import {getCernBoxPersonalLink, getCernBoxProjectLink} from 'state/selectors';
import eosIcon from 'static/eos_icon.png';

import WebEOSCreationForm from './WebEOSCreationForm';

import styles from './WebEOSCreation.module.scss';

const exampleIndexHtmlFile = (
  <pre>
    {`<!DOCTYPE html>
<html>
<body>
  <h1>Welcome to my site</h1>
  <p>Some very interesting content.</p>
</body>
</html>`}
  </pre>
);

const WebEOSCreation = () => {
  const [showForm, setShowForm] = useState(true);
  const [createdSiteInfo, setCreatedSiteInfo] = useState({name: null, url: null});
  const [creationError, setCreationError] = useState(null);

  const cernBoxPersonalLink = useSelector(getCernBoxPersonalLink);
  const cernBoxProjectLink = useSelector(getCernBoxProjectLink);

  const createWebEOSSite = async newSiteData => {
    setCreationError(null);
    try {
      await httpClient.post('/api/webeos/sites', newSiteData, {handleErrorsCentrally: false});
      setCreatedSiteInfo({
        name: newSiteData.name,
        url: new URL(`https://${newSiteData.name}${newSiteData.subdomain}`),
      });
      setShowForm(false);
    } catch (err) {
      let creationErrorMessage = '';
      if (err.response.data.errors) {
        creationErrorMessage += JSON.stringify(err.response.data.errors.json);
      }
      if (err.response.data.error) {
        creationErrorMessage += err.response.data.error;
      }
      setCreationError(creationErrorMessage);
    }
  };

  return (
    <Grid centered>
      <Grid.Column className={styles.WebEOSCreation}>
        <Container>
          <Header as="h3" className={styles.Header}>
            <Image src={eosIcon} alt="eos icon" className={styles.HeaderIcon} /> Creating a WebEOS
            site
          </Header>
          {showForm ? (
            <div>
              <p>You're minutes away from getting your site up and running.</p>
              <List>
                <List.Item>
                  1. WebEOS sites have their content stored on EOS. Share the EOS location of your
                  choice with user <code>a:wwweos</code> via the cernbox web interface.
                  <br />
                  See how:
                  <a href={cernBoxPersonalLink} target="_blank" rel="noreferrer">
                    {' '}
                    for personal sites <Icon name="external alternate" size="small" />
                  </a>
                  ,
                  <a href={cernBoxProjectLink} target="_blank" rel="noreferrer">
                    {' '}
                    for project sites <Icon name="external alternate" size="small" />
                  </a>
                </List.Item>
                <List.Item>
                  2. Create <code>index.html</code> file in the EOS location.{' '}
                  <Popup
                    trigger={<span className={styles.Link}>See example</span>}
                    on="click"
                    offset={[0, 50]}
                    position="right center"
                    inverted
                    content={exampleIndexHtmlFile}
                  />
                </List.Item>
                <List.Item>3. Fill in the form below and let us do the magic</List.Item>
              </List>
            </div>
          ) : (
            <div>
              <p>The site has been created.</p>
              <p>
                It may take a few minutes until it is available due to the DNS propagation delay.
              </p>
              <p>
                You can then check how it looks:{' '}
                <a href={createdSiteInfo.url}>{createdSiteInfo.url.toString()}</a>.
              </p>
              <p>
                You can also further{' '}
                <Link to={`webeos/${createdSiteInfo.name}`}>configure your site</Link>.
              </p>
            </div>
          )}
        </Container>
        {creationError && (
          <Message negative>
            <Message.Header>There was a problem creating the site</Message.Header>
            <p>{creationError}</p>
            <p>
              Please try again or open a{' '}
              <a
                href="https://cern.service-now.com/service-portal?id=service_element&name=web-portal"
                target="_blank"
                rel="noreferrer"
              >
                ticket
              </a>
            </p>
          </Message>
        )}
        {showForm && <WebEOSCreationForm create={createWebEOSSite} />}
      </Grid.Column>
    </Grid>
  );
};

export default WebEOSCreation;
